import { useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
import type { DateValidationError } from '@mui/x-date-pickers';

import type { DateFieldConfig } from '../../config';
import type { SectionKey } from '../../lease-details.types';
import { useLeaseField } from '../../hooks/use-lease-field';
import { LeaseDatePicker } from '../../inputs/lease-date-input';
import { useLeaseDetailsDateLinking } from '../../lease-details-date-linking.context';
import { useLeaseDetailsUI } from '../../lease-details-ui.context';
import { useFieldEventContextBuilder } from '../use-field-event-context';
import { deriveLeaseEnd, formatStoredDate } from '../utils';
import { useClearDateWhenDisabled } from './use-clear-date-when-disabled';
import { createDataTestId } from '@/utils/createDataTestId';

const INVALID_DATE_ERROR_TYPE = 'mui-invalid-date';
const DEFAULT_INVALID_DATE_MESSAGE = 'Invalid date';

export type DateFieldProps = {
  path: string;
  field: DateFieldConfig;
  sectionKey: SectionKey;
  arrayIndex?: number;
  required: boolean;
  disabled: boolean;
  label: ReactNode;
  helperText: ReactNode;
};

export function DateField({
  path,
  field,
  sectionKey,
  arrayIndex,
  required,
  disabled,
  label,
  helperText,
}: DateFieldProps) {
  const { setValue, trigger, getFieldState, setError, clearErrors } = useFormContext();
  const boundField = useLeaseField<string | null | undefined>(path);
  const { inputSize: size, asteriskColor } = useLeaseDetailsUI();
  const linkedDates = useLeaseDetailsDateLinking();

  const raw = boundField.value;
  // Parse once per stored value: a fresh Dayjs every render re-fed the buffered picker (LLX-4088).
  const value = useMemo(() => {
    const parsed = raw ? dayjs(raw) : null;
    return parsed?.isValid() ? parsed : null;
  }, [raw]);
  const error = boundField.error;

  const buildContext = useFieldEventContextBuilder<string | null>(path, sectionKey, arrayIndex);
  const dateLinkingConfig = linkedDates?.config;
  const isLinkedStartDate = path === dateLinkingConfig?.startField;
  const isLinkedEndDate = path === dateLinkingConfig?.endField;
  const isLinkedDateField = isLinkedStartDate || isLinkedEndDate;
  const hasInvalidPickerTextRef = useRef(false);
  // Renewal-only: clear the linked end date when month-to-month disables it; re-derive on re-enable.
  useClearDateWhenDisabled(path, disabled, Boolean(field.clearWhenDisabled));

  const setInvalidDateError = () => {
    setError(path, {
      type: INVALID_DATE_ERROR_TYPE,
      message: field.invalidDateMessage ?? DEFAULT_INVALID_DATE_MESSAGE,
    });
  };

  const formatLinkedDateValue = (next: dayjs.Dayjs | null): string | null =>
    formatStoredDate(next, dateLinkingConfig?.valueFormat);

  // One validation pass for both dates: their rules depend on each other (end after start).
  const triggerLinkedFields = () => {
    if (!dateLinkingConfig?.startField || !dateLinkingConfig?.endField) {
      return;
    }
    void trigger([dateLinkingConfig.startField, dateLinkingConfig.endField]);
  };

  const handleChange = (next: dayjs.Dayjs | null) => {
    if (next !== null && !next.isValid()) {
      return;
    }
    hasInvalidPickerTextRef.current = false;

    if (isLinkedDateField && dateLinkingConfig?.startField && dateLinkingConfig?.endField) {
      const stored = formatLinkedDateValue(next);
      const trackedValue = stored ?? '';
      if (isLinkedStartDate) {
        dateLinkingConfig.onStartDateChanged?.(trackedValue);
        setValue(dateLinkingConfig.startField, stored, { shouldDirty: true, shouldTouch: true });
        field.events?.onChange?.(buildContext(stored));

        const keepManualEnd =
          !dateLinkingConfig.overwriteManualEnd && linkedDates?.endDateManuallyEdited.current;
        if (next && !keepManualEnd) {
          const derivedEndDate = deriveLeaseEnd(next, dateLinkingConfig.leaseDurationMonths);
          const derivedStored = formatLinkedDateValue(derivedEndDate);
          setValue(dateLinkingConfig.endField, derivedStored, { shouldDirty: true });
        }
      } else if (isLinkedEndDate) {
        dateLinkingConfig.onEndDateChanged?.(trackedValue);
        if (linkedDates) {
          linkedDates.endDateManuallyEdited.current = next !== null;
        }
        setValue(dateLinkingConfig.endField, stored, { shouldDirty: true, shouldTouch: true });
        field.events?.onChange?.(buildContext(stored));
      }

      triggerLinkedFields();
      return;
    }

    const stored = next ? next.toISOString() : null;
    boundField.onChange(stored);
    field.events?.onChange?.(buildContext(stored));
  };

  // Unparseable input never reaches `handleChange`; other codes are owned by the schema.
  const handleError = (err: DateValidationError) => {
    if (err === 'invalidDate') {
      hasInvalidPickerTextRef.current = true;
      setInvalidDateError();
      return;
    }
    hasInvalidPickerTextRef.current = false;
    const existing = getFieldState(path).error;
    if (existing?.type === INVALID_DATE_ERROR_TYPE) {
      clearErrors(path);
    }
  };

  // MUI doesn't publish for partial input (e.g. `12/12/YYYY`); trigger so resolver-owned
  // errors (like required) can win. Re-apply invalidDate only when schema passes.
  const handleBlur = () => {
    void trigger(path).then((isValid) => {
      if (isValid && hasInvalidPickerTextRef.current) {
        setInvalidDateError();
      }
    });
  };

  // `!= null` (not truthiness) so an offset of 0 (min = today) still applies — 0 is falsy. Only the
  // renewal flow passes 0/negative; edit/invite pass 365 or omit it, so they behave the same.
  const { minDateOffsetDays, maxDateOffsetDays } = field;
  const minDate = useMemo(
    () => (minDateOffsetDays != null ? dayjs().subtract(minDateOffsetDays, 'day') : undefined),
    [minDateOffsetDays],
  );
  const maxDate = useMemo(
    () => (maxDateOffsetDays ? dayjs().add(maxDateOffsetDays, 'day') : undefined),
    [maxDateOffsetDays],
  );

  const dataTestId = createDataTestId({ inputString: path, suffix: 'input' });

  return (
    <LeaseDatePicker
      title={typeof label === 'string' ? label : ''}
      value={value}
      onChange={handleChange}
      onError={handleError}
      onBlur={handleBlur}
      inputRef={boundField.ref}
      error={error}
      required={required}
      disabled={disabled}
      dataTestId={dataTestId}
      minDate={minDate}
      maxDate={maxDate}
      size={size}
      redAsterisk={asteriskColor == null}
      asteriskColor={asteriskColor}
      helperText={helperText}
    />
  );
}
